import { useState, useEffect } from 'react'
import {
  subscribeToFlows,
  createFlow,
  updateFlow,
  deleteFlow,
  createItemId
} from '../firebase/flows'
import { isUserAdmin, refreshUserToken } from '../firebase/auth'
import './Flows.css'

const Flows = ({ user }) => {
  const [flows, setFlows] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [error, setError] = useState(null)
  const [copyFeedback, setCopyFeedback] = useState(null)

  // Create flow form
  const [showNewFlow, setShowNewFlow] = useState(false)
  const [newFlowName, setNewFlowName] = useState('')
  const [newFlowDescription, setNewFlowDescription] = useState('')

  // Edit flow
  const [editingFlowId, setEditingFlowId] = useState(null)
  const [editFlowName, setEditFlowName] = useState('')
  const [editFlowDescription, setEditFlowDescription] = useState('')

  // Add item form (within a flow)
  const [addingItemFlowId, setAddingItemFlowId] = useState(null)
  const [newItemLabel, setNewItemLabel] = useState('')
  const [newItemTemplate, setNewItemTemplate] = useState('')

  // Edit item
  const [editingItemKey, setEditingItemKey] = useState(null) // 'flowId_itemId'
  const [editItemLabel, setEditItemLabel] = useState('')
  const [editItemTemplate, setEditItemTemplate] = useState('')

  useEffect(() => {
    if (!user) return
    const checkAdmin = async () => {
      await refreshUserToken(user)
      const admin = await isUserAdmin(user, true)
      setIsAdmin(admin)
    }
    checkAdmin()
  }, [user])

  useEffect(() => {
    const unsub = subscribeToFlows((data) => {
      setFlows(data || [])
      setLoading(false)
    })
    return () => unsub()
  }, [])

  const handleCreateFlow = async (e) => {
    e.preventDefault()
    setError(null)
    const name = newFlowName.trim()
    if (!name) {
      setError('Flow name is required.')
      return
    }
    try {
      await createFlow({ name, description: newFlowDescription.trim(), items: [] })
      setNewFlowName('')
      setNewFlowDescription('')
      setShowNewFlow(false)
    } catch (err) {
      setError(err.message || 'Failed to create flow.')
    }
  }

  const handleUpdateFlow = async (flowId) => {
    setError(null)
    const name = editFlowName.trim()
    if (!name) {
      setError('Flow name is required.')
      return
    }
    try {
      await updateFlow(flowId, {
        name,
        description: editFlowDescription.trim() || null
      })
      setEditingFlowId(null)
      setEditFlowName('')
      setEditFlowDescription('')
    } catch (err) {
      setError(err.message || 'Failed to update flow.')
    }
  }

  const handleDeleteFlow = async (flow) => {
    if (!window.confirm(`Delete flow "${flow.name}"? This cannot be undone.`)) return
    setError(null)
    try {
      await deleteFlow(flow.id)
    } catch (err) {
      setError(err.message || 'Failed to delete flow.')
    }
  }

  const startEditFlow = (flow) => {
    setEditingFlowId(flow.id)
    setEditFlowName(flow.name || '')
    setEditFlowDescription(flow.description || '')
    setError(null)
  }

  const cancelEditFlow = () => {
    setEditingFlowId(null)
    setEditFlowName('')
    setEditFlowDescription('')
  }

  const addItemToFlow = async (flowId, e) => {
    e.preventDefault()
    setError(null)
    const label = newItemLabel.trim()
    if (!label) {
      setError('Step label is required.')
      return
    }
    const flow = flows.find(f => f.id === flowId)
    if (!flow) return
    const items = Array.isArray(flow.items) ? [...flow.items] : []
    items.push({
      id: createItemId(),
      label,
      templateText: (newItemTemplate || '').trim(),
      order: items.length
    })
    try {
      await updateFlow(flowId, { items })
      setNewItemLabel('')
      setNewItemTemplate('')
      setAddingItemFlowId(null)
    } catch (err) {
      setError(err.message || 'Failed to add step.')
    }
  }

  const updateItemInFlow = async (flowId, itemId) => {
    setError(null)
    const flow = flows.find(f => f.id === flowId)
    if (!flow) return
    const items = (flow.items || []).map(it =>
      it.id === itemId
        ? { ...it, label: editItemLabel.trim(), templateText: (editItemTemplate || '').trim() }
        : it
    )
    try {
      await updateFlow(flowId, { items })
      setEditingItemKey(null)
      setEditItemLabel('')
      setEditItemTemplate('')
    } catch (err) {
      setError(err.message || 'Failed to update step.')
    }
  }

  const removeItemFromFlow = async (flowId, item) => {
    if (!window.confirm(`Remove "${item.label}" from this flow?`)) return
    const flow = flows.find(f => f.id === flowId)
    if (!flow) return
    const items = (flow.items || []).filter(it => it.id !== item.id)
    try {
      await updateFlow(flowId, { items })
    } catch (err) {
      setError(err.message || 'Failed to remove step.')
    }
  }

  const startEditItem = (flowId, item) => {
    setEditingItemKey(`${flowId}_${item.id}`)
    setEditItemLabel(item.label || '')
    setEditItemTemplate(item.templateText || '')
    setError(null)
  }

  const cancelEditItem = () => {
    setEditingItemKey(null)
    setEditItemLabel('')
    setEditItemTemplate('')
  }

  const copyToClipboard = (text) => {
    if (!text) return
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback('Copied!')
      setTimeout(() => setCopyFeedback(null), 2000)
    }).catch(() => setCopyFeedback('Copy failed'))
  }

  if (loading) {
    return <div className="flows-loading">Loading flows...</div>
  }

  return (
    <div className="flows-page">
      <div className="flows-page-header">
        <div>
          <h2>Customer Flows</h2>
          <p className="flows-subtitle">
            Template messages and follow-up steps for supporting paying customers. Copy template text to send to customers or remind yourself of required actions.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setShowNewFlow(true)
              setNewFlowName('')
              setNewFlowDescription('')
              setError(null)
            }}
          >
            + New Flow
          </button>
        )}
      </div>

      {error && <div className="flows-error">{error}</div>}
      {copyFeedback && <div className="flows-copy-feedback">{copyFeedback}</div>}

      {showNewFlow && isAdmin && (
        <div className="flows-form-card">
          <h3>Create flow</h3>
          <form onSubmit={handleCreateFlow}>
            <div className="form-group">
              <label>Flow name</label>
              <input
                value={newFlowName}
                onChange={(e) => setNewFlowName(e.target.value)}
                placeholder="e.g. Onboarding, Renewal reminder"
              />
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <input
                value={newFlowDescription}
                onChange={(e) => setNewFlowDescription(e.target.value)}
                placeholder="When to use this flow"
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowNewFlow(false); setError(null); }}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">Create</button>
            </div>
          </form>
        </div>
      )}

      {flows.length === 0 && !showNewFlow ? (
        <div className="flows-empty">
          <p>No flows yet. {isAdmin ? 'Create a flow to define template messages and steps for your team.' : 'Ask an admin to add customer flows.'}</p>
        </div>
      ) : (
        <div className="flows-list">
          {flows.map((flow) => (
            <div key={flow.id} className="flow-card">
              <div className="flow-card-header">
                {editingFlowId === flow.id ? (
                  <div className="flow-edit-inline">
                    <input
                      value={editFlowName}
                      onChange={(e) => setEditFlowName(e.target.value)}
                      placeholder="Flow name"
                      className="flow-edit-name"
                    />
                    <input
                      value={editFlowDescription}
                      onChange={(e) => setEditFlowDescription(e.target.value)}
                      placeholder="Description (optional)"
                      className="flow-edit-desc"
                    />
                    <div className="flow-edit-actions">
                      <button type="button" className="btn-icon btn-save" onClick={() => handleUpdateFlow(flow.id)} title="Save">✓</button>
                      <button type="button" className="btn-icon btn-cancel" onClick={cancelEditFlow} title="Cancel">×</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="flow-toggle"
                      onClick={() => setExpandedId(expandedId === flow.id ? null : flow.id)}
                      aria-expanded={expandedId === flow.id}
                    >
                      <span className="flow-toggle-icon">{expandedId === flow.id ? '▼' : '▶'}</span>
                      <span className="flow-name">{flow.name}</span>
                      {flow.description && <span className="flow-desc-inline"> — {flow.description}</span>}
                    </button>
                    {isAdmin && (
                      <div className="flow-actions">
                        <button type="button" className="btn-icon btn-edit" onClick={() => startEditFlow(flow)} title="Edit flow">✏️</button>
                        <button type="button" className="btn-icon btn-delete" onClick={() => handleDeleteFlow(flow)} title="Delete flow">🗑️</button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {expandedId === flow.id && (
                <div className="flow-body">
                  <ul className="flow-items-list">
                    {(flow.items || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((item, idx) => (
                      <li key={item.id} className="flow-item">
                        {editingItemKey === `${flow.id}_${item.id}` ? (
                          <div className="flow-item-edit">
                            <input
                              value={editItemLabel}
                              onChange={(e) => setEditItemLabel(e.target.value)}
                              placeholder="Step label"
                              className="flow-item-label-input"
                            />
                            <textarea
                              value={editItemTemplate}
                              onChange={(e) => setEditItemTemplate(e.target.value)}
                              placeholder="Template text (e.g. email or reminder)"
                              className="flow-item-template-input"
                              rows={3}
                            />
                            <div className="flow-item-edit-actions">
                              <button type="button" className="btn-secondary btn-sm" onClick={() => updateItemInFlow(flow.id, item.id)}>Save</button>
                              <button type="button" className="btn-secondary btn-sm" onClick={cancelEditItem}>Cancel</button>
                              {isAdmin && (
                                <button type="button" className="btn-icon btn-delete" onClick={() => removeItemFromFlow(flow.id, item)} title="Remove step">🗑️</button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flow-item-content">
                              <span className="flow-item-number">{idx + 1}.</span>
                              <span className="flow-item-label">{item.label}</span>
                              {item.templateText && (
                                <div className="flow-item-template">
                                  <pre>{item.templateText}</pre>
                                  <button
                                    type="button"
                                    className="btn-copy"
                                    onClick={() => copyToClipboard(item.templateText)}
                                  >
                                    Copy
                                  </button>
                                </div>
                              )}
                            </div>
                            {isAdmin && (
                              <div className="flow-item-actions">
                                <button type="button" className="btn-icon btn-edit" onClick={() => startEditItem(flow.id, item)} title="Edit step">✏️</button>
                                <button type="button" className="btn-icon btn-delete" onClick={() => removeItemFromFlow(flow.id, item)} title="Remove step">🗑️</button>
                              </div>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>

                  {isAdmin && (
                    <>
                      {addingItemFlowId === flow.id ? (
                        <form className="flow-add-item-form" onSubmit={(e) => addItemToFlow(flow.id, e)}>
                          <div className="form-group">
                            <label>Step label</label>
                            <input
                              value={newItemLabel}
                              onChange={(e) => setNewItemLabel(e.target.value)}
                              placeholder="e.g. Send welcome email / Remind to follow up"
                            />
                          </div>
                          <div className="form-group">
                            <label>Template text (optional)</label>
                            <textarea
                              value={newItemTemplate}
                              onChange={(e) => setNewItemTemplate(e.target.value)}
                              placeholder="Paste or type template message for staff to copy"
                              rows={3}
                            />
                          </div>
                          <div className="form-actions">
                            <button type="button" className="btn-secondary" onClick={() => { setAddingItemFlowId(null); setNewItemLabel(''); setNewItemTemplate(''); }}>Cancel</button>
                            <button type="submit" className="btn-primary">Add step</button>
                          </div>
                        </form>
                      ) : (
                        <button
                          type="button"
                          className="flow-add-item-btn"
                          onClick={() => { setAddingItemFlowId(flow.id); setNewItemLabel(''); setNewItemTemplate(''); setError(null); }}
                        >
                          + Add step
                        </button>
                      )}
                    </>
                  )}

                  {(flow.items || []).length === 0 && !addingItemFlowId && (
                    <p className="flow-no-items">No steps yet. {isAdmin ? 'Add a step (e.g. template message or reminder).' : ''}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Flows
